import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { flushSync } from "react-dom";
import { CATEGORIES } from "../data/catalogue";
import type {
  AnswersImportPayload,
  CatalogueItem,
  FilterKey,
  LocalSession,
  Participant,
  ParticipantAnswer,
  RemoteRoomView,
  Seat,
  SharedNote,
  TakeLocalRoomOnlineOptions,
} from "../types";
import {
  allItems,
  countSavedAnswers,
  daysRemaining,
  otherSeat,
} from "../lib/session";
import {
  buildSessionExport,
} from "../lib/export";
import { FibonacciSpiral } from "./FibonacciSpiral";
import {
  CatalogueRail,
  type CatalogueItemSummary,
} from "./CatalogueRail";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  BrowseIcon,
  ExpiryIcon,
  ExportIcon,
  FollowIcon,
  InviteIcon,
  NextIncompleteIcon,
  ProfileIcon,
  ShieldIcon,
  TrashIcon,
} from "./Icons";
import { ItemWorkspace } from "./ItemWorkspace";
import { LocalItemWorkspace } from "./LocalItemWorkspace";
import {
  ConfirmModal,
  CustomEntryModal,
  ExportModal,
  InviteModal,
  Modal,
  PrintableReport,
  ProfileModal,
  TakeOnlineModal,
} from "./Modals";
import { BrowseToolbar } from "./ReviewViews";

export interface BoardActions {
  saveAnswer: (answer: ParticipantAnswer) => Promise<void>;
  importAnswers: (payload: AnswersImportPayload) => Promise<void>;
  saveAnswerForSeat?: (
    seat: Seat,
    answer: ParticipantAnswer,
  ) => Promise<void>;
  saveNote: (note: SharedNote) => Promise<void>;
  setActiveItem: (itemId: string) => void;
  updateProfile: (participant: Participant) => Promise<void>;
  addCustom: (item: CatalogueItem) => Promise<void>;
  updateCustom: (item: CatalogueItem) => Promise<void>;
  deleteCustom: (item: CatalogueItem) => void;
  deleteRoom: () => void;
  resetExpiry: () => boolean;
  setLocalRetention?: (retainedIndefinitely: boolean) => boolean;
  clearError: () => void;
}

type OpenModal =
  | "custom"
  | "profile"
  | "invite"
  | "export"
  | "online"
  | "menu"
  | "delete"
  | null;

type BoardMode = "answer" | "follow" | "browse";
type WorkMode = Exclude<BoardMode, "browse">;

type ViewTransitionDocument = Document & {
  startViewTransition?: (update: () => void) => {
    finished: Promise<unknown>;
  };
};

export function Board({
  session,
  actions,
  error,
  partnerInvite,
  onTakeOnline,
  onShowPrivacy,
}: {
  session: LocalSession | RemoteRoomView;
  actions: BoardActions;
  error: string;
  partnerInvite: string | null;
  onTakeOnline?: (options: TakeLocalRoomOnlineOptions) => Promise<void>;
  onShowPrivacy: () => void;
}) {
  const [openModal, setOpenModal] = useState<OpenModal>(null);
  const [mode, setMode] = useState<BoardMode>("answer");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [focusBrowseSearch, setFocusBrowseSearch] = useState(false);
  const browseReturnModeRef = useRef<WorkMode>("answer");
  const modeRef = useRef<BoardMode>("answer");
  const activeViewTransitionRef = useRef<{
    finished: Promise<unknown>;
  } | null>(null);
  const queuedModeUpdateRef = useRef<(() => void) | null>(null);
  modeRef.current = mode;
  const transitionMode = useCallback(
    (nextMode: BoardMode, prepare?: () => void) => {
      const applyMode = () => {
        prepare?.();
        setMode(nextMode);
      };
      const viewTransitionDocument = document as ViewTransitionDocument;
      const crossesBrowseBoundary =
        (modeRef.current === "browse") !== (nextMode === "browse");
      if (
        !crossesBrowseBoundary ||
        !viewTransitionDocument.startViewTransition ||
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
      ) {
        applyMode();
        return;
      }
      if (activeViewTransitionRef.current) {
        queuedModeUpdateRef.current = applyMode;
        return;
      }
      const transition = viewTransitionDocument.startViewTransition(() => {
        flushSync(applyMode);
      });
      activeViewTransitionRef.current = transition;
      const finishTransition = () => {
        if (activeViewTransitionRef.current !== transition) return;
        activeViewTransitionRef.current = null;
        const queuedUpdate = queuedModeUpdateRef.current;
        queuedModeUpdateRef.current = null;
        if (queuedUpdate) flushSync(queuedUpdate);
      };
      void transition.finished.then(finishTransition, finishTransition);
    },
    [],
  );
  const [collapsedCategoryIds, setCollapsedCategoryIds] = useState<
    Set<string>
  >(() => readCollapsedCategories(session.roomId));

  useEffect(() => {
    document.documentElement.dataset.input = "keyboard";
    const onPointer = () => {
      document.documentElement.dataset.input = "pointer";
    };
    const onKeyboard = (event: globalThis.KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      document.documentElement.dataset.input = "keyboard";
    };
    document.addEventListener("pointerdown", onPointer, { passive: true });
    document.addEventListener("keydown", onKeyboard);
    return () => {
      document.removeEventListener("pointerdown", onPointer);
      document.removeEventListener("keydown", onKeyboard);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem(
      `feel-scene:categories:${session.roomId}`,
      JSON.stringify([...collapsedCategoryIds]),
    );
  }, [session.roomId, collapsedCategoryIds]);

  const ownSeat: Seat = session.mode === "local" ? "A" : session.seat;
  const partnerSeat = otherSeat(ownSeat);
  const ownAnswers =
    session.mode === "local" ? session.answers[ownSeat] : session.ownAnswers;
  const rawPartnerAnswers =
    session.mode === "local"
      ? session.answers[partnerSeat]
      : session.partnerAnswers;
  const partnerSavedIds = new Set(
    session.mode === "local"
      ? Object.values(rawPartnerAnswers)
          .filter((answer) => answer.savedAt !== null)
          .map((answer) => answer.itemId)
      : [
          ...session.partnerSaved,
          ...Object.keys(session.partnerAnswers),
        ],
  );
  const partnerSavedKey = [...partnerSavedIds].sort().join("|");
  const items = useMemo(
    () => allItems(session.customEntries),
    [session.customEntries],
  );
  const deferredSearch = useDeferredValue(search);
  const ownActiveItemId = session.activeItemId;
  const [browseAnchorItemId, setBrowseAnchorItemId] = useState(
    ownActiveItemId,
  );
  const ownParticipant = session.participants[ownSeat];
  const partnerParticipant = session.participants[partnerSeat];
  const itemSummaries = useMemo(() => {
    const summaries = new Map<string, CatalogueItemSummary>();
    for (const item of items) {
      const own = ownAnswers[item.id];
      const partner = rawPartnerAnswers[item.id];
      const ownSaved = Boolean(own?.savedAt);
      const partnerSaved = partnerSavedIds.has(item.id);
      const revealed =
        session.mode === "local" || (ownSaved && partnerSaved);
      summaries.set(item.id, {
        ownAnswer: ownSaved && own ? own : null,
        partnerAnswer: revealed && partnerSaved && partner ? partner : null,
        partnerState:
          revealed && partnerSaved && partner
            ? "visible"
            : partnerSaved
              ? "saved"
              : "none",
        ownSaved,
        rowImportant: Boolean(session.notes[item.id]?.important),
        note: session.notes[item.id] ?? null,
      });
    }
    return summaries;
  }, [
    items,
    ownAnswers,
    rawPartnerAnswers,
    partnerSavedKey,
    session.notes,
  ]);

  const visibleItems = useMemo(() => {
    const query = deferredSearch.trim().toLocaleLowerCase();
    return items.filter((item) => {
      const own = ownAnswers[item.id];
      const partner = rawPartnerAnswers[item.id];
      const note = session.notes[item.id];
      const ownSaved = Boolean(own?.savedAt);
      const partnerSaved = partnerSavedIds.has(item.id);
      const revealed =
        session.mode === "local" || (ownSaved && partnerSaved);
      if (filter === "unanswered" && ownSaved) return false;
      if (filter === "pending" && !(ownSaved !== partnerSaved)) return false;
      if (
        filter === "differences" &&
        !(
          revealed &&
          partner &&
          answerDiffers(own, partner)
        )
      ) {
        return false;
      }
      if (
        filter === "notes" &&
        !(
          note?.text.trim() ||
          own?.context.trim() ||
          (revealed && partner?.context.trim())
        )
      ) {
        return false;
      }
      if (
        filter === "important" &&
        !(
          note?.important ||
          own?.important ||
          (revealed && partner?.important)
        )
      ) {
        return false;
      }
      if (!query) return true;
      const searchable = [
        item.term,
        item.definition,
        item.aliases.join(" "),
        item.categoryName,
        own?.context ?? "",
        note?.text ?? "",
        revealed ? partner?.context ?? "" : "",
      ]
        .join(" ")
        .toLocaleLowerCase();
      return searchable.includes(query);
    });
  }, [
    items,
    deferredSearch,
    filter,
    ownAnswers,
    rawPartnerAnswers,
    session.notes,
    partnerSavedKey,
  ]);

  const latestRevealedPartnerItemId = useMemo(() => {
    if (session.mode !== "remote") return null;
    let latest: ParticipantAnswer | null = null;
    for (const answer of Object.values(rawPartnerAnswers)) {
      if (!answer.savedAt) continue;
      if (!latest || answer.updatedAt > latest.updatedAt) latest = answer;
    }
    return latest?.itemId ?? null;
  }, [rawPartnerAnswers, session.mode]);
  const [followItemId, setFollowItemId] = useState(
    session.mode === "remote"
      ? session.partnerActiveItemId ??
          latestRevealedPartnerItemId ??
          ownActiveItemId
      : ownActiveItemId,
  );
  useEffect(() => {
    if (session.mode !== "remote") return;
    if (session.partnerActiveItemId) {
      setFollowItemId(session.partnerActiveItemId);
    } else if (latestRevealedPartnerItemId) {
      setFollowItemId(latestRevealedPartnerItemId);
    }
  }, [
    latestRevealedPartnerItemId,
    session.mode,
    session.mode === "remote" ? session.partnerActiveItemId : null,
  ]);

  const displayedItemId =
    mode === "follow" && session.mode === "remote"
      ? followItemId
      : ownActiveItemId;
  const activeItem =
    items.find((item) => item.id === displayedItemId) ?? items[0];
  const activeItemIndex = items.findIndex((item) => item.id === activeItem.id);
  const nextItem = items[activeItemIndex + 1];
  const nextCategoryId =
    nextItem && nextItem.categoryId !== activeItem.categoryId
      ? nextItem.categoryId
      : null;

  useEffect(() => {
    setCollapsedCategoryIds((current) => {
      if (
        !current.has(activeItem.categoryId) &&
        (nextCategoryId === null || !current.has(nextCategoryId))
      ) {
        return current;
      }
      const next = new Set(current);
      next.delete(activeItem.categoryId);
      if (nextCategoryId !== null) next.delete(nextCategoryId);
      return next;
    });
  }, [activeItem.categoryId, nextCategoryId]);

  const ownAnswer = ownAnswers[activeItem.id] ?? null;
  const rawPartnerAnswer = rawPartnerAnswers[activeItem.id] ?? null;
  const partnerSaved = partnerSavedIds.has(activeItem.id);
  const partnerAnswer =
    session.mode === "local"
      ? rawPartnerAnswer
      : ownAnswer?.savedAt && partnerSaved
        ? rawPartnerAnswer
        : null;
  const ownComplete = items.every((item) => {
    if (!ownAnswers[item.id]?.savedAt) return false;
    return (
      session.mode !== "local" || Boolean(rawPartnerAnswers[item.id]?.savedAt)
    );
  });
  const savedCount = countSavedAnswers(ownAnswers, items);
  const exportData = useMemo(() => buildSessionExport(session), [session]);
  const beforeNavigateRef = useRef<(() => Promise<void>) | null>(null);
  const activeItemIdRef = useRef(ownActiveItemId);
  activeItemIdRef.current = ownActiveItemId;
  const navigationQueueRef = useRef<Promise<void>>(Promise.resolve());

  const openExport = useCallback(async () => {
    await beforeNavigateRef.current?.();
    setOpenModal("export");
  }, []);

  const registerBeforeNavigate = useCallback(
    (handler: (() => Promise<void>) | null) => {
      beforeNavigateRef.current = handler;
    },
    [],
  );

  const selectItem = useCallback((itemId: string) => {
    const navigate = async () => {
      if (itemId === activeItemIdRef.current) return;
      await beforeNavigateRef.current?.();
      const item = items.find((candidate) => candidate.id === itemId);
      if (item) {
        setCollapsedCategoryIds((current) => {
          if (!current.has(item.categoryId)) return current;
          const next = new Set(current);
          next.delete(item.categoryId);
          return next;
        });
      }
      activeItemIdRef.current = itemId;
      actions.setActiveItem(itemId);
    };
    const queued = navigationQueueRef.current.then(navigate, navigate);
    navigationQueueRef.current = queued;
    return queued;
  }, [actions.setActiveItem, items]);

  const toggleRowImportant = useCallback(async (itemId: string) => {
    const current = session.notes[itemId];
    await actions.saveNote({
      itemId,
      text: current?.text ?? "",
      important: !current?.important,
      updatedAt: Date.now(),
      version: current?.version ?? 0,
    });
  }, [actions.saveNote, session.notes]);

  const toggleCategory = (targetCategoryId: string) => {
    setCollapsedCategoryIds((current) => {
      const next = new Set(current);
      if (next.has(targetCategoryId)) next.delete(targetCategoryId);
      else next.add(targetCategoryId);
      return next;
    });
  };

  const moveBy = async (direction: -1 | 1) => {
    const index = items.findIndex((item) => item.id === ownActiveItemId);
    const next = items[index + direction];
    if (next) await selectItem(next.id);
    else if (modeRef.current === "follow") {
      await beforeNavigateRef.current?.();
    }
    setMode("answer");
  };

  const openBrowse = async (focusSearch = false) => {
    await beforeNavigateRef.current?.();
    if (mode !== "browse") {
      browseReturnModeRef.current = mode;
      const state =
        history.state && typeof history.state === "object"
          ? history.state
          : {};
      if (state.feelSceneBrowseRoom !== session.roomId) {
        history.pushState(
          { ...state, feelSceneBrowseRoom: session.roomId },
          "",
          window.location.href,
        );
      }
    }
    setBrowseAnchorItemId(displayedItemId);
    transitionMode("browse", () => {
      setFocusBrowseSearch(focusSearch);
    });
  };

  const closeBrowse = useCallback((returnMode?: WorkMode) => {
    const nextMode = returnMode ?? browseReturnModeRef.current;
    browseReturnModeRef.current = nextMode;
    if (history.state?.feelSceneBrowseRoom === session.roomId) {
      history.back();
      return;
    }
    transitionMode(nextMode, () => {
      setFocusBrowseSearch(false);
    });
  }, [session.roomId, transitionMode]);

  const nextUnanswered = () => {
    const firstIncomplete = items.find((item) => {
      if (!ownAnswers[item.id]?.savedAt) return true;
      return (
        session.mode === "local" &&
        !rawPartnerAnswers[item.id]?.savedAt
      );
    });
    if (!firstIncomplete) return;
    setBrowseAnchorItemId(firstIncomplete.id);
    void selectItem(firstIncomplete.id).then(() => {
      if (modeRef.current === "browse") closeBrowse("answer");
      else setMode("answer");
    });
  };

  const showAnswer = async () => {
    if (mode === "browse") {
      closeBrowse("answer");
      return;
    }
    if (mode === "follow") await beforeNavigateRef.current?.();
    setMode("answer");
  };

  const showFollow = async () => {
    if (session.mode !== "remote") return;
    await beforeNavigateRef.current?.();
    setFollowItemId(
      session.partnerActiveItemId ??
        latestRevealedPartnerItemId ??
        ownActiveItemId,
    );
    if (mode === "browse") closeBrowse("follow");
    else setMode("follow");
  };

  useEffect(() => {
    const onPopState = () => {
      if (history.state?.feelSceneBrowseRoom === session.roomId) {
        transitionMode("browse");
        return;
      }
      if (modeRef.current === "browse") {
        transitionMode(browseReturnModeRef.current, () => {
          setFocusBrowseSearch(false);
        });
      }
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [session.roomId, transitionMode]);

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || modeRef.current !== "browse" || openModal) {
        return;
      }
      event.preventDefault();
      closeBrowse();
    };
    document.addEventListener("keydown", onEscape);
    return () => document.removeEventListener("keydown", onEscape);
  }, [closeBrowse, openModal]);

  const wasCompleteRef = useRef(ownComplete);
  useEffect(() => {
    const justCompleted = !wasCompleteRef.current && ownComplete;
    wasCompleteRef.current = ownComplete;
    if (!justCompleted || modeRef.current !== "answer") return;
    if (session.mode === "remote") {
      setFollowItemId(
        session.partnerActiveItemId ??
          latestRevealedPartnerItemId ??
          ownActiveItemId,
      );
      setMode("follow");
    } else {
      void openBrowse();
    }
  }, [
    latestRevealedPartnerItemId,
    ownActiveItemId,
    ownComplete,
    session.mode,
    session.mode === "remote" ? session.partnerActiveItemId : null,
  ]);

  const ownActiveIndex = items.findIndex(
    (item) => item.id === ownActiveItemId,
  );
  const railItems = mode === "browse" ? visibleItems : items;

  return (
    <div className={`board-shell mode-${mode}`}>
      <FibonacciSpiral className="board-spiral" />
      <header className="board-header">
        <button className="wordmark-button" onClick={() => setOpenModal("menu")}>
          Feel Scene
        </button>
        <nav className="mobile-navigation" aria-label="Catalogue navigation">
          {session.mode === "local" ? (
            <button
              type="button"
              aria-label="First unanswered question"
              onClick={nextUnanswered}
            >
              <NextIncompleteIcon />
            </button>
          ) : session.partnerActiveItemId !== null ? (
            <button
              type="button"
              aria-label={
                mode === "follow"
                  ? "Return to your answers"
                  : `Follow ${partnerParticipant.name}`
              }
              aria-pressed={mode === "follow"}
              onClick={() => {
                if (mode === "follow") void showAnswer();
                else void showFollow();
              }}
            >
              <FollowIcon />
            </button>
          ) : null}
          <button
            type="button"
            aria-label={mode === "browse" ? "Back from browse" : "Browse catalogue"}
            aria-pressed={mode === "browse"}
            onClick={() => {
              if (mode === "browse") closeBrowse();
              else void openBrowse(true);
            }}
          >
            <BrowseIcon />
          </button>
        </nav>
        <div className="room-title">
          <strong>{session.participants.A.name} + {session.participants.B.name}</strong>
        </div>
        <BrowseToolbar
          browse={mode === "browse"}
          search={search}
          filter={filter}
          focusSearch={focusBrowseSearch}
          onSearch={(value) => {
            setSearch(value);
            if (mode !== "browse" && value.trim()) void openBrowse();
          }}
          onFilter={setFilter}
          onAdd={() => setOpenModal("custom")}
        />
        <nav className="board-modes" aria-label="Board mode">
          <button
            type="button"
            className={mode === "answer" ? "selected" : ""}
            aria-pressed={mode === "answer"}
            onClick={() => void showAnswer()}
          >
            Answer
          </button>
          {session.mode === "remote" ? (
            <button
              type="button"
              className={mode === "follow" ? "selected" : ""}
              aria-pressed={mode === "follow"}
              onClick={() => void showFollow()}
            >
              Follow
            </button>
          ) : null}
          <button
            type="button"
            className={mode === "browse" ? "selected" : ""}
            aria-pressed={mode === "browse"}
            onClick={() => void openBrowse()}
          >
            Browse
          </button>
        </nav>
        <div className="participant-state">
          <button className="text-button" onClick={() => setOpenModal("profile")}>
            {ownParticipant.name} · {stanceLabel(ownParticipant.stance)}
          </button>
        </div>
        <div className="board-progress">
          <span>{savedCount} / {items.length}</span>
          <span>
            {session.mode === "local" && session.retainedIndefinitely
              ? "Kept indefinitely"
              : `${daysRemaining(session.expiresAt)} days left`}
          </span>
        </div>
        <nav className="board-actions" aria-label="Room actions">
          {!ownComplete ? (
            <button className="text-button" onClick={nextUnanswered}>
              Next unanswered
            </button>
          ) : null}
        </nav>
      </header>

      {mode !== "browse" ? (
        <nav className="mobile-step-navigation" aria-label="Question navigation">
          <button
            type="button"
            aria-label="Previous question"
            disabled={mode === "answer" && ownActiveIndex <= 0}
            onClick={() => void moveBy(-1)}
          >
            <ArrowUpIcon />
          </button>
          <button
            type="button"
            aria-label="Next question"
            disabled={mode === "answer" && ownActiveIndex >= items.length - 1}
            onClick={() => void moveBy(1)}
          >
            <ArrowDownIcon />
          </button>
        </nav>
      ) : null}

      <main className="board-main">
        {railItems.length ? (
          <CatalogueRail
              items={railItems}
              mode={mode}
              activeItemId={mode === "browse" ? null : displayedItemId}
              transitionItemId={
                mode === "browse" ? browseAnchorItemId : displayedItemId
              }
              partnerActiveItemId={
                session.mode === "remote"
                  ? session.partnerActiveItemId
                  : null
              }
              ownName={ownParticipant.name}
              partnerName={partnerParticipant.name}
              itemSummaries={itemSummaries}
              collapsedCategoryIds={collapsedCategoryIds}
              onSelect={(itemId) => {
                if (mode === "browse") {
                  setBrowseAnchorItemId(itemId);
                  void selectItem(itemId).then(() => closeBrowse("answer"));
                } else if (mode === "follow") {
                  void (async () => {
                    await beforeNavigateRef.current?.();
                    setMode("answer");
                    await selectItem(itemId);
                  })();
                } else {
                  void selectItem(itemId);
                }
              }}
              onToggleRowImportant={toggleRowImportant}
              onToggleCategory={toggleCategory}
              activeContent={
                mode === "browse" ? null : session.mode === "local" && actions.saveAnswerForSeat ? (
                  <LocalItemWorkspace
                    key={activeItem.id}
                    roomId={session.roomId}
                    item={activeItem}
                    participants={session.participants}
                    answers={{
                      A: session.answers.A[activeItem.id] ?? null,
                      B: session.answers.B[activeItem.id] ?? null,
                    }}
                    note={session.notes[activeItem.id] ?? null}
                    onSaveAnswer={actions.saveAnswerForSeat}
                    onSaveNote={actions.saveNote}
                    onAdvance={() => {
                      if (modeRef.current === "answer") void moveBy(1);
                    }}
                    onRegisterBeforeNavigate={registerBeforeNavigate}
                    onDeleteCustom={
                      activeItem.custom &&
                      !activeItem.locked &&
                      activeItem.createdBy === ownSeat
                        ? actions.deleteCustom
                        : null
                    }
                  />
                ) : (
                  <ItemWorkspace
                    key={activeItem.id}
                    roomId={session.roomId}
                    mode="remote"
                    item={activeItem}
                    ownSeat={ownSeat}
                    ownParticipant={ownParticipant}
                    partnerParticipant={partnerParticipant}
                    ownAnswer={ownAnswer}
                    partnerAnswer={partnerAnswer}
                    partnerSaved={partnerSaved}
                    note={session.notes[activeItem.id] ?? null}
                    onSaveAnswer={actions.saveAnswer}
                    onSaveNote={actions.saveNote}
                    onAdvance={() => {
                      if (modeRef.current === "answer") void moveBy(1);
                    }}
                    onRegisterBeforeNavigate={registerBeforeNavigate}
                    onHandoff={null}
                    onDeleteCustom={
                      activeItem.custom &&
                      !activeItem.locked &&
                      activeItem.createdBy === ownSeat
                        ? actions.deleteCustom
                        : null
                    }
                  />
                )
              }
            />
        ) : (
          <section className="empty-results glass-panel">
            <h1>No items match</h1>
            <p>Clear the search or choose another filter.</p>
            <button
              className="button secondary"
              type="button"
              onClick={() => {
                setSearch("");
                setFilter("all");
              }}
            >
              Show all items
            </button>
          </section>
        )}
      </main>

      {error ? (
        <div className="error-toast" role="alert">
          <span>{error}</span>
          <button className="icon-button" aria-label="Dismiss error" onClick={actions.clearError}>×</button>
        </div>
      ) : null}

      {openModal === "custom" ? (
        <CustomEntryModal
          seat={ownSeat}
          onAdd={async (item) => {
            await actions.addCustom(item);
            setOpenModal(null);
            closeBrowse("answer");
          }}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      {openModal === "profile" ? (
        <ProfileModal
          participant={ownParticipant}
          onSave={actions.updateProfile}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      {openModal === "invite" && partnerInvite ? (
        <InviteModal
          partnerInvite={partnerInvite}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      {openModal === "export" ? (
        <ExportModal
          data={exportData}
          onImport={actions.importAnswers}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      {openModal === "online" && session.mode === "local" && onTakeOnline ? (
        <TakeOnlineModal
          session={session}
          onCreate={onTakeOnline}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      {openModal === "menu" ? (
        <RoomMenu
          local={session.mode === "local"}
          retainedIndefinitely={
            session.mode === "local" && Boolean(session.retainedIndefinitely)
          }
          canInvite={Boolean(partnerInvite)}
          connectionStatus={
            session.mode === "local"
              ? "On this device"
              : session.connected
                ? "Synced"
                : "Reconnecting"
          }
          connectionOffline={session.mode === "remote" && !session.connected}
          expiresAt={session.expiresAt}
          onBrowse={() => {
            setOpenModal(null);
            void openBrowse();
          }}
          onProfile={() => setOpenModal("profile")}
          onInvite={() => setOpenModal("invite")}
          onTakeOnline={
            session.mode === "local" && onTakeOnline
              ? () => setOpenModal("online")
              : undefined
          }
          onExport={() => void openExport()}
          onPrivacy={onShowPrivacy}
          onResetExpiry={actions.resetExpiry}
          onSetLocalRetention={actions.setLocalRetention}
          onDelete={() => setOpenModal("delete")}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      {openModal === "delete" ? (
        <ConfirmModal
          title={session.mode === "local" ? "Clear this session?" : "Delete this room?"}
          message={
            session.mode === "local"
              ? "This removes both people’s local answers from this browser."
              : "This disconnects both people and permanently clears the room."
          }
          confirmLabel={session.mode === "local" ? "Clear session" : "Delete room"}
          onConfirm={actions.deleteRoom}
          onClose={() => setOpenModal(null)}
        />
      ) : null}
      <PrintableReport data={exportData} />
    </div>
  );
}

function RoomMenu({
  local,
  retainedIndefinitely,
  canInvite,
  connectionStatus,
  connectionOffline,
  expiresAt,
  onBrowse,
  onProfile,
  onInvite,
  onTakeOnline,
  onExport,
  onPrivacy,
  onResetExpiry,
  onSetLocalRetention,
  onDelete,
  onClose,
}: {
  local: boolean;
  retainedIndefinitely: boolean;
  canInvite: boolean;
  connectionStatus: string;
  connectionOffline: boolean;
  expiresAt: number;
  onBrowse: () => void;
  onProfile: () => void;
  onInvite: () => void;
  onTakeOnline?: () => void;
  onExport: () => void;
  onPrivacy: () => void;
  onResetExpiry: () => boolean;
  onSetLocalRetention?: (retainedIndefinitely: boolean) => boolean;
  onDelete: () => void;
  onClose: () => void;
}) {
  const [resetFrom, setResetFrom] = useState<number | null>(null);
  const resetComplete = resetFrom !== null && expiresAt > resetFrom;
  const resetting = resetFrom !== null && !resetComplete;

  return (
    <Modal title="Room" onClose={onClose} className="compact-modal room-menu">
      <div className="room-menu-status" role="status">
        <span className={`sync-state ${connectionOffline ? "offline" : ""}`}>
          <i aria-hidden="true" />
          {connectionStatus}
        </span>
      </div>
      <nav>
        <button className="menu-action" onClick={onBrowse}>
          <BrowseIcon />
          <span className="menu-action-copy">
            <span>Browse</span>
            <small>Categories, search, and filters</small>
          </span>
        </button>
        {canInvite ? (
          <button className="menu-action" onClick={onInvite}>
            <InviteIcon />
            <span className="menu-action-copy">
              <span>Partner invite</span>
              <small>Copy the private link again</small>
            </span>
          </button>
        ) : null}
        {local && onTakeOnline ? (
          <button className="menu-action primary-action" onClick={onTakeOnline}>
            <InviteIcon />
            <span className="menu-action-copy">
              <span>Take this session online</span>
              <small>Copy one or both sides into a private room</small>
            </span>
          </button>
        ) : null}
        <button className="menu-action" onClick={onProfile}>
          <ProfileIcon />
          <span className="menu-action-copy">
            <span>Your defaults</span>
            <small>Change name or starting stance</small>
          </span>
        </button>
        <button className="menu-action" onClick={onExport}>
          <ExportIcon />
          <span className="menu-action-copy">
            <span>Export &amp; import</span>
            <small>Print or reuse your answers as CSV</small>
          </span>
        </button>
        {local ? (
          <button
            className="menu-action"
            onClick={() =>
              onSetLocalRetention?.(!retainedIndefinitely)
            }
          >
            <ExpiryIcon />
            <span className="menu-action-copy">
              <span>
                {retainedIndefinitely
                  ? "Use 30-day retention"
                  : "Keep indefinitely"}
              </span>
              <small>
                {retainedIndefinitely
                  ? "Stored only on this device until you clear it"
                  : `Currently expires ${formatRoomExpiry(expiresAt)}`}
              </small>
            </span>
          </button>
        ) : (
          <button
            className="menu-action"
            disabled={connectionOffline || resetting}
            onClick={() => {
              const previousExpiry = expiresAt;
              if (onResetExpiry()) setResetFrom(previousExpiry);
            }}
          >
            <ExpiryIcon />
            <span className="menu-action-copy">
              <span>
                {resetComplete
                  ? "Expiry reset"
                  : resetting
                    ? "Resetting expiry…"
                    : "Reset expiry to 30 days"}
              </span>
              <small>
                {connectionOffline
                  ? "Reconnect to change the expiry"
                  : `Expires ${formatRoomExpiry(expiresAt)}`}
              </small>
            </span>
          </button>
        )}
        <a
          className="menu-action"
          href="/privacy-security"
          onClick={(event) => {
            event.preventDefault();
            onPrivacy();
          }}
        >
          <ShieldIcon />
          <span className="menu-action-copy">
            <span>Privacy and security</span>
            <small>How rooms work, strengths, and risks</small>
          </span>
        </a>
        <button className="menu-action danger" onClick={onDelete}>
          <TrashIcon />
          <span className="menu-action-copy">
            <span>{local ? "Clear session" : "Delete room"}</span>
            <small>This cannot be undone</small>
          </span>
        </button>
      </nav>
    </Modal>
  );
}

function formatRoomExpiry(expiresAt: number) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(expiresAt);
}

function readCollapsedCategories(roomId: string) {
  try {
    const raw = localStorage.getItem(`feel-scene:categories:${roomId}`);
    if (!raw) {
      return new Set(CATEGORIES.slice(1).map((category) => category.id));
    }
    const parsed = JSON.parse(raw) as string[];
    return new Set(parsed);
  } catch {
    return new Set(CATEGORIES.slice(1).map((category) => category.id));
  }
}

function answerDiffers(
  first: ParticipantAnswer | null | undefined,
  second: ParticipantAnswer | null | undefined,
) {
  if (!first || !second) return false;
  const keys = new Set([
    ...Object.keys(first.perspectives),
    ...Object.keys(second.perspectives),
  ]);
  for (const key of keys) {
    const a = first.perspectives[key];
    const b = second.perspectives[key];
    if (
      !a ||
      !b ||
      a.enabled !== b.enabled ||
      a.interest !== b.interest ||
      a.experience !== b.experience
    ) {
      return true;
    }
  }
  return false;
}

function stanceLabel(stance: Participant["stance"]) {
  if (stance === "dominant") return "Dominant";
  if (stance === "submissive") return "Submissive";
  return "Switch";
}
